import { Button } from '@orbind/ui'
import { useEffect, useState } from 'react'
import { NOME_DO_SISTEMA, sistemaOperacional, urlDeDownload, type SistemaOperacional } from '../lib/platform.ts'
import { apiOrigin } from '../lib/api.ts'
import './download.css'

type Release = {
  id: string
  platform: string
  version: string
  format: string
  fileName: string
  sizeBytes: number
  checksum: string
  notes: string | null
  createdAt: string
}

/**
 * Pagina de download do Orbind Desktop.
 *
 * Fica FORA do shell autenticado: quem chega aqui pode nem ter conta, e o
 * instalador nao e segredo. O botao principal ja vem apontado para o sistema de
 * quem acessa; as outras versoes ficam listadas abaixo para quem baixa para
 * outra maquina.
 */
export function DownloadPage() {
  const [releases, setReleases] = useState<Release[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const sistema = sistemaOperacional()

  useEffect(() => {
    fetch(`${apiOrigin()}/download/releases`)
      .then(async (r) => {
        if (!r.ok) throw new Error('Não foi possível consultar as versões disponíveis.')
        return (await r.json()) as { releases: Release[] }
      })
      .then((d) => setReleases(d.releases))
      .catch((e) => setErro(e instanceof Error ? e.message : 'Falha ao consultar as versões.'))
  }, [])

  const paraMim = releases?.find((r) => r.platform === sistema) ?? null

  return (
    <div className="dl">
      <div className="dl__caixa">
        <h1 className="dl__titulo">Orbind Desktop</h1>
        <p className="dl__texto">
          O aplicativo traz o Orbind para a sua máquina e abre as aplicações que você cadastrou direto na área de
          trabalho — algo que o navegador não permite a uma página.
        </p>

        {erro ? (
          <div className="dl__aviso">
            <i className="fas fa-exclamation-triangle" aria-hidden />
            <span>{erro}</span>
          </div>
        ) : releases === null ? (
          <div className="dl__aviso">
            <i className="fas fa-circle-notch fa-spin" aria-hidden />
            <span>Consultando versões…</span>
          </div>
        ) : releases.length === 0 ? (
          <div className="dl__aviso">
            <i className="fas fa-box-open" aria-hidden />
            <span>Nenhuma versão publicada ainda. Assim que houver uma, ela aparece aqui.</span>
          </div>
        ) : (
          <>
            {paraMim ? (
              <div className="dl__principal">
                <Button variant="primary" onClick={() => window.open(urlDeDownload(sistema), '_self')}>
                  <i className="fas fa-download" aria-hidden style={{ marginRight: 8 }} />
                  Baixar para {NOME_DO_SISTEMA[sistema]}
                </Button>
                <span className="dl__meta">
                  versão {paraMim.version} · {tamanho(paraMim.sizeBytes)} · {paraMim.fileName}
                </span>
                {/*
                  O app do Mac ainda vai sem a assinatura da Apple
                  (SPEC-20261016-2136-desktop-para-mac): na primeira abertura o
                  macOS bloqueia e só libera pelo Ajustes do Sistema.
                */}
                {sistema === 'macos' ? (
                  <span className="dl__meta">
                    Abra o arquivo baixado e arraste o Orbind para Aplicativos. Na primeira vez, se o macOS avisar que
                    não pode verificar o desenvolvedor, vá em Ajustes do Sistema → Privacidade e Segurança e clique em
                    "Abrir Mesmo Assim".
                  </span>
                ) : null}
              </div>
            ) : (
              <div className="dl__aviso">
                <i className="fas fa-info-circle" aria-hidden />
                <span>
                  Ainda não há versão para {NOME_DO_SISTEMA[sistema]}. As disponíveis estão listadas abaixo.
                </span>
              </div>
            )}

            <ul className="dl__lista">
              {releases.map((r) => (
                <li key={r.id} className="dl__item">
                  <div className="dl__item-txt">
                    <span className="dl__item-nome">{rotuloDaPlataforma(r.platform)}</span>
                    <span className="dl__item-meta">
                      versão {r.version} · {tamanho(r.sizeBytes)} · publicada em{' '}
                      {new Date(r.createdAt).toLocaleDateString('pt-BR')}
                    </span>
                    {r.notes ? <span className="dl__item-notas">{r.notes}</span> : null}
                  </div>
                  <a className="dl__item-btn" href={urlDeDownload(r.platform as SistemaOperacional)}>
                    Baixar
                  </a>
                </li>
              ))}
            </ul>
          </>
        )}

        <a className="dl__voltar" href="/">
          Voltar ao Orbind
        </a>
      </div>
    </div>
  )
}

function rotuloDaPlataforma(valor: string) {
  return valor === 'windows' ? 'Windows' : valor === 'macos' ? 'macOS' : valor === 'linux' ? 'Linux' : valor
}

function tamanho(bytes: number) {
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}
